/**
 * Centralized localStorage manager with typed getters/setters.
 * All localStorage access should go through this module.
 */

import { LanguageLevel } from '../types/database'

// Storage key constants
export const STORAGE_KEYS = {
  VOICE_SETTINGS: 'voiceSettings',
  MAX_CARDS_IN_ROTATION: 'maxCardsInRotation',
  LEVEL_SETTINGS: 'levelSettings',
  SHOW_SYNC_STATUS: 'showSyncStatus',
  LAST_RESYNC_CHECK: 'lastResyncCheck',
  HIDE_HELP_HINT: 'hideHelpHint',
  STUDY_SELECTED_TAGS: 'studySelectedTags',
  STUDY_SELECTED_EXERCISES: 'studySelectedExercises',
  // Dynamic key prefix for sequential study position
  SEQUENTIAL_POSITION_PREFIX: 'sequential_position_',
  // Selected study area
  SELECTED_STUDY_AREA: 'selectedStudyArea',
} as const

// Type definitions for stored values
export interface VoiceSettings {
  [languageCode: string]: string // voice URI or "voiceName|index"
}

export interface LevelSettings {
  enabledLevels: LanguageLevel[]
  filterMode: 'only' | 'prefer'
}

// Default values
const DEFAULTS = {
  maxCardsInRotation: 30,
  levelSettings: {
    enabledLevels: ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as LanguageLevel[],
    filterMode: 'only' as const,
  },
  showSyncStatus: false,
  hideHelpHint: false,
  voiceSettings: {} as VoiceSettings,
  studySelectedTags: [] as string[],
  studySelectedExercises: [] as string[],
}

// Generic helpers
function getJSON<T>(key: string, defaultValue: T): T {
  try {
    const saved = localStorage.getItem(key)
    return saved ? JSON.parse(saved) : defaultValue
  } catch {
    return defaultValue
  }
}

function setJSON<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value))
}

// Voice Settings
export function getVoiceSettings(): VoiceSettings {
  return getJSON(STORAGE_KEYS.VOICE_SETTINGS, DEFAULTS.voiceSettings)
}

export function setVoiceSettings(settings: VoiceSettings): void {
  setJSON(STORAGE_KEYS.VOICE_SETTINGS, settings)
}

export function getVoiceForLanguage(languageCode: string): string | null {
  const settings = getVoiceSettings()
  return settings[languageCode] || null
}

export function setVoiceForLanguage(languageCode: string, voiceUri: string): void {
  const settings = getVoiceSettings()
  settings[languageCode] = voiceUri
  setVoiceSettings(settings)
}

// Rotation Size
export function getMaxCardsInRotation(): number {
  const saved = localStorage.getItem(STORAGE_KEYS.MAX_CARDS_IN_ROTATION)
  return saved ? parseInt(saved, 10) : DEFAULTS.maxCardsInRotation
}

export function setMaxCardsInRotation(count: number): void {
  localStorage.setItem(STORAGE_KEYS.MAX_CARDS_IN_ROTATION, count.toString())
}

// Level Settings
export function getLevelSettings(): LevelSettings {
  return getJSON(STORAGE_KEYS.LEVEL_SETTINGS, DEFAULTS.levelSettings)
}

export function setLevelSettings(settings: LevelSettings): void {
  setJSON(STORAGE_KEYS.LEVEL_SETTINGS, settings)
}

// Sync Status Display
export function getShowSyncStatus(): boolean {
  return getJSON(STORAGE_KEYS.SHOW_SYNC_STATUS, DEFAULTS.showSyncStatus)
}

export function setShowSyncStatus(show: boolean): void {
  setJSON(STORAGE_KEYS.SHOW_SYNC_STATUS, show)
}

// Last Resync Check
export function getLastResyncCheck(): string | null {
  return localStorage.getItem(STORAGE_KEYS.LAST_RESYNC_CHECK)
}

export function setLastResyncCheck(timestamp: string): void {
  localStorage.setItem(STORAGE_KEYS.LAST_RESYNC_CHECK, timestamp)
}

// Help Hint
export function getHideHelpHint(): boolean {
  return getJSON(STORAGE_KEYS.HIDE_HELP_HINT, DEFAULTS.hideHelpHint)
}

export function setHideHelpHint(hide: boolean): void {
  setJSON(STORAGE_KEYS.HIDE_HELP_HINT, hide)
}

// Study Selections
export function getStudySelectedTags(): string[] {
  return getJSON(STORAGE_KEYS.STUDY_SELECTED_TAGS, DEFAULTS.studySelectedTags)
}

export function setStudySelectedTags(tags: string[]): void {
  setJSON(STORAGE_KEYS.STUDY_SELECTED_TAGS, tags)
}

export function getStudySelectedExercises(): string[] {
  return getJSON(STORAGE_KEYS.STUDY_SELECTED_EXERCISES, DEFAULTS.studySelectedExercises)
}

export function setStudySelectedExercises(exercises: string[]): void {
  setJSON(STORAGE_KEYS.STUDY_SELECTED_EXERCISES, exercises)
}

// Sequential Study Position (dynamic key based on user and tags)
export function getSequentialPosition(userId: string, tags: string[]): number {
  const key = `${STORAGE_KEYS.SEQUENTIAL_POSITION_PREFIX}${userId}_${tags.sort().join('_')}`
  const saved = localStorage.getItem(key)
  return saved ? parseInt(saved, 10) : 0
}

export function setSequentialPosition(userId: string, tags: string[], position: number): void {
  const key = `${STORAGE_KEYS.SEQUENTIAL_POSITION_PREFIX}${userId}_${tags.sort().join('_')}`
  localStorage.setItem(key, position.toString())
}

// Selected Study Area
export function getSelectedStudyArea(): string | null {
  return localStorage.getItem(STORAGE_KEYS.SELECTED_STUDY_AREA)
}

export function setSelectedStudyArea(areaId: string | null): void {
  if (areaId) {
    localStorage.setItem(STORAGE_KEYS.SELECTED_STUDY_AREA, areaId)
  } else {
    localStorage.removeItem(STORAGE_KEYS.SELECTED_STUDY_AREA)
  }
}
